import React, { ReactNode, useState } from 'react';
import {
  AlertIcon,
  CheckMarkIcon,
  EditIcon,
  StatusWarningIcon,
  Warning,
} from '@dynamika-shared/ui/icons';
import { Spinner } from '@dynamika-shared/ui/Spinner/Spinner';
import { Button } from '@dynamika-shared/ui/Button/Button';
import {
  CheckStatus,
  CheckStatusTitle,
  CheckStatusToTitleMap,
} from '@components/SiteUserUpload/types/uploadTypes';
import { twJoin, twMerge } from '@utils/twMerge';
import { VerificationStepsItem } from 'src/store-redux/siteUpload/siteUploadStore.types';
import { getStatusColorClass } from '@components/SiteUserUpload/helpers/uploadHelpers';
import { TextArea } from '@dynamika-shared/ui/TextArea/TextArea';

type FileProcessingStatusLineProps = {
  statusOption: VerificationStepsItem;
  setVerificationStepsData: (steps?: Partial<VerificationStepsItem>[]) => void;
  verificationStepsData: Partial<VerificationStepsItem>[] | undefined;
  setDisableConfirmUpload: (value: boolean) => void;
  handleReupload: () => void;
};

enum DicomErrors {
  DICOM_FORMAT_CHECK = 'DICOM file format check',
}

export const FileProcessingStatusLine: React.FC<
  FileProcessingStatusLineProps
> = ({
  statusOption,
  setVerificationStepsData,
  verificationStepsData,
  setDisableConfirmUpload,
  handleReupload,
}) => {
  const defaultSize = 'h-2 w-2';

  const [inputValue, setInputValue] = useState('');
  const [confirmedComment, setConfirmedComment] = useState<boolean>(false);

  const statusClass = getStatusColorClass(
    !confirmedComment
      ? (statusOption.status as CheckStatus)
      : CheckStatus.RESOLVED
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
  };

  const handleConfirmComment = () => {
    setVerificationStepsData(
      verificationStepsData?.map((item: Partial<VerificationStepsItem>) => {
        if (item.id === statusOption.id) {
          return {
            comment: inputValue.trim(),
            id: item.id,
            type: item.type,
          };
        }
        return item;
      })
    );
    setConfirmedComment(true);
  };

  const statusColorMap: Record<CheckStatus, ReactNode> = {
    [CheckStatus.NO_VALIDATED]: (
      <span
        className={twMerge(
          'mr-1 block rounded-full border border-pastelBlue',
          defaultSize
        )}
      ></span>
    ),
    [CheckStatus.SUCCESS]: (
      <CheckMarkIcon
        className={twMerge(
          'mr-1 rounded-full text-white bg-yellowGreen',
          defaultSize
        )}
      />
    ),
    [CheckStatus.RESOLVED]: (
      <CheckMarkIcon
        className={twMerge(
          'mr-1 rounded-full text-white bg-honoluluBlue',
          defaultSize
        )}
      />
    ),
    [CheckStatus.WARNING]: (
      <StatusWarningIcon className={twMerge(defaultSize, 'mr-1')} />
    ),
    [CheckStatus.LOADING]: (
      <Spinner
        spinColor={'transparent'}
        spinCircleColor={'black'}
        className={defaultSize}
      />
    ),
  };

  const handleEditClick = () => {
    setDisableConfirmUpload(true);
    setConfirmedComment(false);
  };

  return (
    <div
      className='border-b border-brightGray px-1 py-2.5'
      data-testid='UploadFileProcessingStatus'
    >
      <div className='flex items-center justify-between pl-2'>
        <div className='flex items-center'>
          <p>
            {
              statusColorMap[
                confirmedComment
                  ? CheckStatus.RESOLVED
                  : (statusOption.status as CheckStatus)
              ]
            }
          </p>
          <p
            className={twMerge(
              'text-1424 font-medium',
              statusOption.status === CheckStatus.NO_VALIDATED && statusClass
            )}
          >
            {statusOption.title}
          </p>
        </div>
        <p className={`text-1216 ${statusClass}`}>
          {!confirmedComment
            ? CheckStatusTitle[
                CheckStatusToTitleMap[statusOption.status as CheckStatus]
              ]
            : CheckStatusTitle[CheckStatus.RESOLVED]}
        </p>
      </div>
      {statusOption.status === CheckStatus.WARNING && (
        <div className='mt-2'>
          {!confirmedComment && (
            <div className='flex rounded-3xl border-[1px] border-redOrange bg-seashell px-2 py-1.5 '>
              <div className='mr-1 pt-0.5'>
                <AlertIcon className='h-2 w-2 text-redOrange' />
              </div>
              <p className='text-1424 font-medium'>{statusOption.message}</p>
            </div>
          )}

          {!confirmedComment ? (
            <div className='my-2 flex justify-between'>
              {statusOption.title === DicomErrors.DICOM_FORMAT_CHECK ? (
                <div className='flex w-full justify-end'>
                  <Button
                    className='font-poppins mt-1 flex h-4 min-w-[99px] items-center rounded-2xl border-[1px] border-vermilion !bg-vividTangelo !px-2 py-[6px] text-1420 text-white hover:!border-vermilion'
                    onClick={handleReupload}
                  >
                    Reupload
                  </Button>
                </div>
              ) : (
                <>
                  <h3 className='mt-2 pl-2 text-1424 font-semibold'>Comment</h3>
                  <div className='flex w-1/2 flex-col items-end'>
                    <TextArea
                      value={inputValue}
                      name={statusOption.type}
                      placeholder='Write a comment here'
                      className='mb-1 h-10 min-h-10 !resize-none'
                      classNameInputWrapper='max-w-[100%]'
                      onChange={e => handleInputChange(e)}
                      maxLength={500}
                    />
                    <Button
                      disabled={!inputValue || inputValue.trim() === ''}
                      className={twJoin(
                        inputValue &&
                          inputValue.trim() !== '' &&
                          '!bg-vividTangelo !text-white hover:!border-vermilion border-vermilion',
                        'text-1420 mt-1 flex h-4 min-w-[89px] items-center rounded-2xl '
                      )}
                      onClick={handleConfirmComment}
                    >
                      Confirm
                    </Button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className='mt-1 rounded-2xl border border-brightGray bg-waterLight bg-opacity-30'>
              <div className='flex justify-between border-b-[1px] border-brightGray p-2'>
                <div className='flex items-center'>
                  <Warning className='text-pastelBlue' />
                  <p className='pl-1.5 text-1424 font-medium'>Added Comment</p>
                </div>
                <Button
                  className='flex h-4 w-[74px] cursor-pointer items-center rounded-2xl border-[1px] border-brightGray !bg-white px-2 py-[6px] !text-1420 text-dimGray hover:border-honoluluBlue hover:!bg-dodgerBlue'
                  onClick={handleEditClick}
                  icon={<EditIcon className=' h-[10px] w-[10px]' />}
                >
                  Edit
                </Button>
              </div>
              <div className='flex justify-between p-2'>
                <p className='w-1/2 text-1624 text-dimGray'>Comment</p>
                <p className='w-1/2 text-1624 font-medium'>{inputValue}</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
